import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Table, 
    Modal, 
    Form, 
    Input, 
    Select, 
    message, 
    Space, 
    Tag, 
    Alert,
    Divider,
    Row,
    Col,
    Typography,
    Tabs,
    Statistic,
    Progress,
    Timeline,
    Badge,
    Tooltip,
    Switch,
    InputNumber,
    Upload,
    List,
    Avatar
} from 'antd';
import { 
    SendOutlined, 
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    MailOutlined,
    ClockCircleOutlined,
    UserOutlined,
    SettingOutlined,
    EyeOutlined,
    DownloadOutlined,
    ReloadOutlined,
    BugOutlined,
    ThunderboltOutlined,
    FileTextOutlined,
    TeamOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

function EmailTesting() {
    const [configurations, setConfigurations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [testModalVisible, setTestModalVisible] = useState(false);
    const [testResults, setTestResults] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [form] = Form.useForm();
    const [testStats, setTestStats] = useState({
        totalTests: 0,
        successfulTests: 0,
        failedTests: 0,
        successRate: 0
    });

    useEffect(() => {
        loadConfigurations();
        loadTestResults();
        loadTestStats();
    }, []);

    const loadConfigurations = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetEmailConfigurations();
            setConfigurations(response.data || []);
        } catch (error) {
            message.error('Failed to load email configurations');
        } finally {
            setLoading(false);
        }
    };

    const loadTestResults = async () => {
        try {
            // This would be a new endpoint for test results
            const response = await api.adminGetEmailTestResults();
            setTestResults(response.data || []);
        } catch (error) {
            console.error('Failed to load test results:', error);
        }
    };

    const loadTestStats = async () => {
        try {
            const response = await api.adminGetEmailTestStats();
            setTestStats(response.data || testStats);
        } catch (error) {
            console.error('Failed to load test stats:', error);
        }
    };

    const openTestModal = (config) => {
        setSelectedConfig(config);
        form.setFieldsValue({
            testType: 'basic',
            recipientEmail: config.from_address,
            subject: 'Test Email from Spoon & Spice Admin',
            message: 'This is a test email to verify your email configuration is working correctly.',
            includeTemplate: false,
            templateId: null
        });
        setTestModalVisible(true);
    };

    const handleTestSubmit = async (values) => {
        try {
            setLoading(true);
            
            const testData = {
                configurationId: selectedConfig.id,
                testType: values.testType,
                recipientEmail: values.recipientEmail,
                subject: values.subject,
                message: values.message,
                includeTemplate: values.includeTemplate,
                templateId: values.templateId,
                customData: values.customData || {}
            };

            const response = await api.adminSendTestEmail(testData);
            
            if (response.success) {
                message.success('Test email sent successfully!');
                loadTestResults();
                loadTestStats();
            } else {
                message.error('Test email failed to send');
            }
            
            setTestModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error(error.message || 'Failed to send test email');
        } finally {
            setLoading(false);
        }
    };

    const handleQuickTest = async (configId) => {
        try {
            setLoading(true);
            const response = await api.adminTestEmailConfiguration(configId);
            if (response.success) {
                message.success('Quick test email sent successfully!');
                loadTestResults();
                loadTestStats();
            } else {
                message.error('Quick test failed');
            }
        } catch (error) {
            message.error(error.message || 'Quick test failed');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkTest = async () => {
        try {
            setLoading(true);
            const response = await api.adminBulkTestEmailConfigurations();
            if (response.success) {
                message.success('Bulk test completed successfully!');
                loadTestResults();
                loadTestStats();
            } else {
                message.error('Bulk test failed');
            }
        } catch (error) {
            message.error(error.message || 'Bulk test failed');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Configuration',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Text strong>{text}</Text>
                    {record.is_default && <Tag color="blue">Default</Tag>}
                    {record.is_active && <Tag color={getStatusColor('success')}>Active</Tag>}
                </Space>
            ),
        },
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
            render: (driver) => <Tag color="purple">{driver.toUpperCase()}</Tag>,
        },
        {
            title: 'From Address',
            dataIndex: 'from_address',
            key: 'from_address',
        },
        {
            title: 'Last Test',
            key: 'lastTest',
            render: (_, record) => {
                const lastTest = testResults.find(r => r.configuration_id === record.id);
                if (!lastTest) return <Text type="secondary">Never tested</Text>;
                
                return (
                    <Space>
                        <Tag color={getStatusColor(lastTest.success ? 'success' : 'cancelled')}>
                            {lastTest.success ? 'Success' : 'Failed'}
                        </Tag>
                        <Text type="secondary">
                            {new Date(lastTest.created_at).toLocaleDateString()}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<ThunderboltOutlined />}
                        onClick={() => handleQuickTest(record.id)}
                        loading={loading}
                    >
                        Quick Test
                    </Button>
                    <Button 
                        size="small" 
                        icon={<SendOutlined />}
                        onClick={() => openTestModal(record)}
                    >
                        Advanced Test
                    </Button>
                </Space>
            ),
        },
    ];

    const testResultColumns = [
        {
            title: 'Configuration',
            dataIndex: 'configuration_name',
            key: 'configuration_name',
        },
        {
            title: 'Test Type',
            dataIndex: 'test_type',
            key: 'test_type',
            render: (type) => <Tag color="blue">{type}</Tag>,
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient_email',
            key: 'recipient_email',
        },
        {
            title: 'Status',
            dataIndex: 'success',
            key: 'success',
            render: (success) => (
                <Tag color={getStatusColor(success ? 'success' : 'cancelled')}>
                    {success ? 'Success' : 'Failed'}
                </Tag>
            ),
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
        },
        {
            title: 'Tested At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString(),
        },
    ];

    const renderTestForm = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                onFinish={handleTestSubmit}
            >
                <Form.Item
                    label="Test Type"
                    name="testType"
                    rules={[{ required: true, message: 'Please select test type' }]}
                >
                    <Select placeholder="Select test type">
                        <Option value="basic">Basic Test</Option>
                        <Option value="template">Template Test</Option>
                        <Option value="bulk">Bulk Test</Option>
                        <Option value="performance">Performance Test</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Recipient Email"
                    name="recipientEmail"
                    rules={[
                        { required: true, message: 'Please enter recipient email' },
                        { type: 'email', message: 'Please enter valid email' }
                    ]}
                >
                    <Input placeholder="test@example.com" />
                </Form.Item>

                <Form.Item
                    label="Subject"
                    name="subject"
                    rules={[{ required: true, message: 'Please enter subject' }]}
                >
                    <Input placeholder="Test Email Subject" />
                </Form.Item>

                <Form.Item
                    label="Message"
                    name="message"
                    rules={[{ required: true, message: 'Please enter message' }]}
                >
                    <TextArea rows={4} placeholder="Test email message content" />
                </Form.Item>

                <Form.Item
                    label="Include Template"
                    name="includeTemplate"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>

                <Form.Item shouldUpdate={(prev, cur) => prev.includeTemplate !== cur.includeTemplate} noStyle>
                    {() => {
                        const includeTemplate = form.getFieldValue('includeTemplate');
                        return includeTemplate ? (
                            <Form.Item
                                label="Template"
                                name="templateId"
                                rules={[{ required: true, message: 'Please select template' }]}
                            >
                                <Select placeholder="Select email template">
                                    <Option value="welcome">Welcome Email</Option>
                                    <Option value="contact-form">Contact Form</Option>
                                    <Option value="booking-confirmation">Booking Confirmation</Option>
                                    <Option value="newsletter">Newsletter</Option>
                                </Select>
                            </Form.Item>
                        ) : null;
                    }}
                </Form.Item>

                <Form.Item
                    label="Custom Data (JSON)"
                    name="customData"
                >
                    <TextArea 
                        rows={3} 
                        placeholder='{"key": "value", "name": "John Doe"}'
                    />
                </Form.Item>
            </Form>
        );
    };

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Email Testing</Title>
                <Text type="secondary">
                    Test email configurations and monitor email delivery performance.
                </Text>
            </div>

            {/* Statistics Cards */}
            <Row gutter={16} className="mb-6">
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Total Tests"
                            value={testStats.totalTests}
                            prefix={<MailOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Successful"
                            value={testStats.successfulTests}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Failed"
                            value={testStats.failedTests}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<ExclamationCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Success Rate"
                            value={testStats.successRate}
                            suffix="%"
                            valueStyle={{ color: testStats.successRate > 80 ? '#3f8600' : '#cf1322' }}
                        />
                        <Progress 
                            percent={testStats.successRate} 
                            size="small" 
                            status={testStats.successRate > 80 ? 'success' : 'exception'}
                        />
                    </Card>
                </Col>
            </Row>

            <Tabs defaultActiveKey="configurations">
                <TabPane tab="Configurations" key="configurations">
                    <Card
                        title="Email Configurations"
                        extra={
                            <Space>
                                <Button 
                                    icon={<ReloadOutlined />}
                                    onClick={loadConfigurations}
                                    loading={loading}
                                >
                                    Refresh
                                </Button>
                                <Button 
                                    type="primary"
                                    icon={<ThunderboltOutlined />}
                                    onClick={handleBulkTest}
                                    loading={loading}
                                >
                                    Bulk Test All
                                </Button>
                            </Space>
                        }
                    >
                        <Table
                            columns={columns}
                            dataSource={configurations}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                            }}
                        />
                    </Card>
                </TabPane>

                <TabPane tab="Test Results" key="results">
                    <Card
                        title="Test Results"
                        extra={
                            <Button 
                                icon={<ReloadOutlined />}
                                onClick={loadTestResults}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        }
                    >
                        <Table
                            columns={testResultColumns}
                            dataSource={testResults}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                            }}
                        />
                    </Card>
                </TabPane>

                <TabPane tab="Performance" key="performance">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Card title="Email Delivery Timeline">
                                <Timeline>
                                    <Timeline.Item color="green">
                                        <Text strong>Configuration 1</Text>
                                        <br />
                                        <Text type="secondary">Last test: 2 minutes ago</Text>
                                    </Timeline.Item>
                                    <Timeline.Item color="blue">
                                        <Text strong>Configuration 2</Text>
                                        <br />
                                        <Text type="secondary">Last test: 5 minutes ago</Text>
                                    </Timeline.Item>
                                    <Timeline.Item color="red">
                                        <Text strong>Configuration 3</Text>
                                        <br />
                                        <Text type="secondary">Last test: 1 hour ago (Failed)</Text>
                                    </Timeline.Item>
                                </Timeline>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Recent Activity">
                                <List
                                    dataSource={testResults.slice(0, 5)}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <List.Item.Meta
                                                avatar={
                                                    <Avatar 
                                                        icon={item.success ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                                                        style={{ backgroundColor: item.success ? '#52c41a' : '#ff4d4f' }}
                                                    />
                                                }
                                                title={
                                                    <Space>
                                                        <Text strong>{item.configuration_name}</Text>
                                                        <Tag color={item.success ? 'green' : 'red'}>
                                                            {item.test_type}
                                                        </Tag>
                                                    </Space>
                                                }
                                                description={
                                                    <Space>
                                                        <Text type="secondary">{item.recipient_email}</Text>
                                                        <Text type="secondary">
                                                            {new Date(item.created_at).toLocaleString()}
                                                        </Text>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Col>
                    </Row>
                </TabPane>
            </Tabs>

            <Modal
                title={`Test Email Configuration - ${selectedConfig?.name}`}
                open={testModalVisible}
                onCancel={() => setTestModalVisible(false)}
                footer={null}
                width={800}
            >
                {renderTestForm()}
                
                <div className="flex justify-end gap-2 mt-6">
                    <Button onClick={() => setTestModalVisible(false)}>
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<SendOutlined />}
                        onClick={() => form.submit()}
                        loading={loading}
                    >
                        Send Test Email
                    </Button>
                </div>
            </Modal>
        </div>
    );
}

export default EmailTesting;
